import {makeAutoObservable} from "mobx";
import {
    ShipmentProcessTypeDto,
    ShipmentTypeDto,
    ShippingConfigurationDataDto,
    ShippingDestinationTypeDto
} from "../../api/fms";
import {validationStore} from "../ValidationStore";
import {equals} from "../../utils";
import {
    api,
    catchApi,
    ShippingConfigConstruct,
    ShippingConfigDestinationConstruct,
    ShipmentTypeWithoutPost, ShippingConfigProcessConstruct
} from "../../api";

function isAllSet(value: ShippingConfigDestinationConstruct): boolean {
    for (const k of Object.keys(value)) {
        const val = value[k as ShipmentTypeWithoutPost]
        if (!val) {
            return false
        }
    }
    return true
}

function allDefined(value: ShippingConfigConstruct) {
    for (const key of Object.keys(value)) {
        const vk = value[key as ShipmentProcessTypeDto]
        for (const key of Object.keys(vk)) {
            const v = vk[key as ShippingDestinationTypeDto]
            const allSet = isAllSet(v)
            if (!allSet) {
                return false
            }
        }
    }
    return true
}

const excludedProcessTypes = [
    ShipmentProcessTypeDto.INTERNAL,
    ShipmentProcessTypeDto.STORAGE_TRANSFER,
    ShipmentProcessTypeDto.STORAGE_TRANSFER_SCA,
    ShipmentProcessTypeDto.STORAGE_TRANSFER_SCA_FINISH
];

const relevantProcessTypes = Object.keys(ShipmentProcessTypeDto)
    .filter(p => !excludedProcessTypes.includes(p as ShipmentProcessTypeDto))

function buildEmpty(): ShippingConfigConstruct {
    const result = {} as ShippingConfigConstruct;
    for (const key of relevantProcessTypes) {
        const k = key as ShipmentProcessTypeDto
        const buf = {} as ShippingConfigProcessConstruct
        for (const key2 of Object.values(ShippingDestinationTypeDto)) {
            const k2 = key2 as ShippingDestinationTypeDto
            const buf2 = {} as ShippingConfigDestinationConstruct
            for (const key3 of Object.values(ShipmentTypeDto)) {
                const k3 = key3 as ShipmentTypeDto
                if (k3 !== ShipmentTypeDto.POST) {
                    buf2[k3] = {
                        value: undefined,
                        defaultValue: undefined
                    }
                }
            }
            buf[k2] = buf2;
        }
        result[k] = buf;
    }
    return result;
}


function buildFilled(data?: ShippingConfigurationDataDto): ShippingConfigConstruct  {
    const result = buildEmpty()
    if (data) {
        const items = data.items
        Object.keys(items).forEach(p => {
            const desti = items[p]
        Object.keys(desti).forEach(d => {
            const v = desti[d]
            Object.keys(v).forEach(t => {
                const val = v[t]
                const process = p as ShipmentProcessTypeDto
                const destination = d as ShippingDestinationTypeDto
                const type = t as ShipmentTypeWithoutPost
                result[process][destination][type] = {
                    value: val,
                    defaultValue: undefined
                }
            })
            })
        })
    }
    return result
}

const empty: ShippingConfigConstruct = buildEmpty()

const systemApi = api.fms.api

class FmsShippingConfigStore {
    get loaded(): ShippingConfigConstruct {
        return this._loaded;
    }

    private set loaded(value: ShippingConfigConstruct) {
        this._loaded = value;
        this.value = value
    }

    get saving(): boolean {
        return this._saving;
    }

    private set saving(value: boolean) {
        this._saving = value;
    }

    get loading(): boolean {
        return this._loading;
    }

    private set loading(value: boolean) {
        this._loading = value;
    }

    get valid(): boolean {
        return this._valid;
    }

    private set valid(value: boolean) {
        this._valid = value;
    }

    get value(): ShippingConfigConstruct {
        return this._value;
    }

    set value(value: ShippingConfigConstruct) {
        this._value = value;
    }

    get allDefined(): boolean {
        return allDefined(this.value)
    }

    get dirty(): boolean {
        return !equals(this.loaded, this.value)
    }

    get savable(): boolean {
        return this.dirty && this.allDefined && !this.saving && !this.loading && this.valid
    }

    private _value: ShippingConfigConstruct = empty
    private _loaded: ShippingConfigConstruct = empty
    private _valid: boolean = false
    private _loading: boolean = false
    private _saving: boolean = false

    constructor() {
        makeAutoObservable(this)
    }

    async load() {

        this.loading = true
        const response = await catchApi(systemApi.getShippingConfiguration)
        this.loaded = buildFilled(response?.data)

        this.loading = false
    }

    async save() {
        this.saving = true
        const dto = this.buildDto()
        const response = await catchApi(
            async () => await systemApi.saveShippingConfiguration(dto)
        )

        this.loaded = buildFilled(response?.data)

        this.saving = false
    }

    async validate() {
        this.valid = false
        const dto = this.buildDto()

        await validationStore.validate(systemApi.validateShippingConfiguration, dto)
        this.valid = Boolean(validationStore.valid)
    }

    private buildDto(): ShippingConfigurationDataDto {
        const items: Record<string, Record<string, Record<string, string>>> = {}

        const value = this.value

        Object.keys(value)
            .map(p => p as ShipmentProcessTypeDto)
            .forEach(process => {

                const dests = value[process]
                if (!items[process]) {
                    items[process] = {}
                }

                Object.keys(dests)
                    .map(d => d as ShippingDestinationTypeDto)
                    .forEach(destination => {
                        const shipmentTypes = dests[destination]
                        if (!items[process][destination]) {
                            items[process][destination] = {}
                        }
                        Object.keys(shipmentTypes)
                            .map(t => t as ShipmentTypeWithoutPost)
                            .forEach(type => {
                                items[process][destination][type] = shipmentTypes[type]?.value || ''
                            })
                    })
            })

        return {
            items
        }
    }
}

const fmsShippingConfigStore = new FmsShippingConfigStore()

export {
    fmsShippingConfigStore
}
